import { Controller } from "stimulus";

const CARET = `<svg class="ms-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 9l-7 7-7-7"/></svg>`;
const MAGNIFIER = `<svg class="ms-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>`;
const CHECK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 13l4 4L19 7"/></svg>`;

export default class extends Controller {
  static targets = ["source", "widget", "badge"];

  static values = { placeholder: String, searchPlaceholder: String };

  declare readonly hasSourceTarget: boolean;
  declare readonly sourceTarget: HTMLSelectElement;
  declare readonly widgetTarget: HTMLElement;
  declare readonly hasBadgeTarget: boolean;
  declare readonly badgeTarget: HTMLElement;

  declare readonly placeholderValue: string;
  declare readonly searchPlaceholderValue: string;

  connect(): void {
    this.build();
    this.sync();
  }

  toggle(event: Event): void {
    event.preventDefault();
    this.setOpen(!this.isOpen);
  }

  // the trigger is a div with role="button", so it has to honour Enter/Space itself
  toggleOnKey(event: KeyboardEvent): void {
    if (event.key !== "Enter" && event.key !== " ") return;
    this.toggle(event);
  }

  removeChip(event: Event): void {
    event.preventDefault();
    event.stopPropagation(); // the chip sits inside the trigger, which would re-open it

    const button = event.currentTarget as HTMLElement;
    const values = this.selectedValues;

    values.delete(button.dataset.value || "");
    this.apply(values);
  }

  optionChanged(event: Event): void {
    const box = event.target as HTMLInputElement;
    if (!box || !box.closest(".ms-opt")) return;

    const values = this.selectedValues;
    box.checked ? values.add(box.value) : values.delete(box.value);
    this.apply(values);
  }

  // "visible" respects the active search filter, as in the design
  selectVisible(event: Event): void {
    event.preventDefault();

    const values = this.selectedValues;
    this.rows.forEach((row) => {
      if (row.hidden) return;
      const box = row.querySelector<HTMLInputElement>("input");
      if (box) values.add(box.value);
    });
    this.apply(values);
  }

  deselectAll(event: Event): void {
    event.preventDefault();
    this.apply(new Set());
  }

  filter(): void {
    this.applyFilter(this.searchInput?.value || "");
  }

  closeOnOutsideClick(event: Event): void {
    if (!this.widgetTarget.contains(event.target as Node)) this.setOpen(false);
  }

  closeOnEscape(event: Event): void {
    if ((event as KeyboardEvent).key === "Escape") this.setOpen(false);
  }

  // auto-save reverts the select in place without firing `change` when a save
  // fails, so re-read it once that controller reports back.
  sync(): void {
    if (!this.hasSourceTarget) return;

    const selected = Array.from(this.sourceTarget.selectedOptions);
    const values = new Set(selected.map((option) => option.value));

    const chips = this.widgetTarget.querySelector<HTMLElement>(".ms-chips");
    if (chips) {
      chips.textContent = "";
      selected.forEach((option) => chips.appendChild(this.buildChip(option)));
    }

    const placeholder = this.widgetTarget.querySelector<HTMLElement>(".ms-placeholder");
    if (placeholder) placeholder.hidden = selected.length > 0;

    this.rows.forEach((row) => {
      const box = row.querySelector<HTMLInputElement>("input");
      if (!box) return;
      box.checked = values.has(box.value);
      row.classList.toggle("checked", box.checked);
    });

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = `${selected.length} selected`;
      this.badgeTarget.hidden = selected.length === 0;
    }
  }

  // A Turbo cache restore brings the built markup back with it, so only build once.
  private build(): void {
    if (!this.hasSourceTarget || this.widgetTarget.querySelector(".ms-trigger")) return;

    // static chrome only — no user data goes through innerHTML
    const chrome = document.createElement("div");
    chrome.innerHTML = `
      <div class="ms-trigger" role="button" tabindex="0" aria-expanded="false" aria-haspopup="listbox"
           data-action="click->multi-select#toggle keydown->multi-select#toggleOnKey">
        <div class="ms-chips"></div>
        <span class="ms-placeholder"></span>
        ${CARET}
      </div>
      <div class="ms-menu" hidden>
        <div class="ms-menu-head">
          <div class="ms-search">
            ${MAGNIFIER}
            <input type="text" class="ms-search-input" autocomplete="off"
                   data-action="input->multi-select#filter">
          </div>
          <div class="ms-actions">
            <button type="button" class="link-action ms-action"
                    data-action="multi-select#selectVisible">${CHECK}Select all visible</button>
            <span class="sep">&middot;</span>
            <button type="button" class="link-action muted ms-action"
                    data-action="multi-select#deselectAll">Deselect all</button>
            <span class="ms-count"></span>
          </div>
        </div>
        <div class="ms-list" role="listbox" aria-multiselectable="true"
             data-action="change->multi-select#optionChanged"></div>
      </div>`;

    while (chrome.firstElementChild) this.widgetTarget.appendChild(chrome.firstElementChild);

    this.widgetTarget.querySelector<HTMLElement>(".ms-placeholder")!.textContent =
      this.placeholderValue || "Select…";
    this.searchInput!.placeholder = this.searchPlaceholderValue || "Search…";

    const list = this.widgetTarget.querySelector<HTMLElement>(".ms-list")!;
    Array.from(this.sourceTarget.options).forEach((option) => {
      const row = document.createElement("label");
      row.className = "ms-opt";

      const box = document.createElement("input");
      box.type = "checkbox";
      box.value = option.value;

      const text = document.createElement("span");
      text.textContent = option.text;

      row.append(box, text);
      list.appendChild(row);
    });

    if (!this.sourceTarget.options.length) {
      const empty = document.createElement("div");
      empty.className = "ms-empty";
      empty.textContent = "No options available.";
      list.appendChild(empty);
    }
  }

  private buildChip(option: HTMLOptionElement): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "ms-chip";
    chip.append(option.text);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "ms-chip-x";
    remove.dataset.value = option.value;
    remove.dataset.action = "multi-select#removeChip";
    remove.setAttribute("aria-label", `Remove ${option.text}`);
    remove.textContent = "×";

    chip.appendChild(remove);
    return chip;
  }

  private apply(values: Set<string>): void {
    if (!this.hasSourceTarget) return;

    Array.from(this.sourceTarget.options).forEach((option) => {
      option.selected = values.has(option.value);
    });
    this.sync();
    this.sourceTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  private applyFilter(term: string): void {
    const needle = term.trim().toLowerCase();
    let visible = 0;

    this.rows.forEach((row) => {
      const match = !needle || (row.textContent || "").toLowerCase().includes(needle);
      row.hidden = !match;
      if (match) visible += 1;
    });

    const count = this.widgetTarget.querySelector<HTMLElement>(".ms-count");
    if (count) count.textContent = `Showing ${visible}`;
  }

  private setOpen(open: boolean): void {
    const menu = this.widgetTarget.querySelector<HTMLElement>(".ms-menu");
    const trigger = this.widgetTarget.querySelector<HTMLElement>(".ms-trigger");
    if (!menu || !trigger) return;

    menu.hidden = !open;
    this.widgetTarget.classList.toggle("open", open);
    trigger.setAttribute("aria-expanded", String(open));

    if (open) {
      this.applyFilter(this.searchInput?.value || "");
      this.searchInput?.focus();
    }
  }

  private get isOpen(): boolean {
    return this.widgetTarget.classList.contains("open");
  }

  private get rows(): HTMLElement[] {
    return Array.from(this.widgetTarget.querySelectorAll<HTMLElement>(".ms-opt"));
  }

  private get searchInput(): HTMLInputElement | null {
    return this.widgetTarget.querySelector<HTMLInputElement>(".ms-search-input");
  }

  private get selectedValues(): Set<string> {
    if (!this.hasSourceTarget) return new Set();
    return new Set(Array.from(this.sourceTarget.selectedOptions).map((option) => option.value));
  }
}
